import { LinkButton } from '@cloudflare/kumo';
import { Select } from '@cloudflare/kumo/components/select';
import { ArrowUpRight } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import { createContext, lazy, Suspense, useContext, type ReactNode } from 'react';
import { sitesQuery, siteQuery } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';

const SiteIdContext = createContext<string | undefined>(undefined);
const Preview = lazy(async () => ({ default: (await import('./site-preview.tsx')).Preview }));
const settingsSections = [
  ['settings', 'General'],
  ['domain', 'Domain'],
  ['header-footer', 'Header & footer'],
  ['redirects', 'Redirects'],
  ['activity', 'Activity'],
  ['sharing', 'Sharing'],
] as const;
const tabs = [
  { title: 'Preview', path: '/sites/$siteId', section: 'preview' },
  { title: 'Pages', path: '/sites/$siteId/pages', section: 'pages' },
  { title: 'Posts', path: '/sites/$siteId/posts', section: 'posts' },
  { title: 'Forms', path: '/sites/$siteId/forms', section: 'forms' },
  { title: 'Assets', path: '/sites/$siteId/assets', section: 'assets' },
  { title: 'Inbox', path: '/sites/$siteId/form-entries', section: 'inbox' },
  { title: 'Blog automation', path: '/sites/$siteId/blog-automation', section: 'blog-automation' },
  { title: 'Site assistant', path: '/sites/$siteId/site-assistant', section: 'site-assistant' },
  { title: 'Settings', path: '/sites/$siteId/settings', section: 'settings' },
  { title: 'Checks', path: '/sites/$siteId/checks', section: 'checks' },
  { title: 'History', path: '/sites/$siteId/history', section: 'history' },
] as const;

export function SitePanel({
  siteId,
  chatId,
  children,
}: {
  siteId?: string;
  chatId?: string;
  children: ReactNode;
}) {
  const sites = useQuery(sitesQuery);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const search = useSearch({ strict: false });
  const screen = pathname.startsWith('/sites/') ? (pathname.split('/')[3] ?? '') : '';
  const section =
    search.preview || !screen
      ? 'preview'
      : screen === 'form-entries' || screen === 'reviews' || pathname.includes('/site-assistant/inbox')
        ? 'inbox'
        : (tabs.find((tab) => tab.section === screen)?.section ?? 'settings');
  const site = sites.data?.find((site) => site.id === siteId);
  return (
    <aside className="site-panel" aria-label="Site panel">
      <div className="site-panel-heading">
        <div className="min-w-0 min-h-6.5 flex flex-1 items-center">
          {sites.isPending ? (
            <span
              role="status"
              aria-label="Loading sites"
              className="block h-4 w-40 max-w-full rounded bg-kumo-tint"
            />
          ) : site && sites.data?.length === 1 ? (
            <span className="block truncate text-sm font-medium" title={site.name}>
              {site.name}
            </span>
          ) : (
            <Select<string>
              size="sm"
              aria-label="Selected site"
              className="w-full min-w-0"
              value={site?.id ?? null}
              placeholder="Choose a site"
              onValueChange={(value) => {
                if (value === null) return;
                void navigate({
                  to: '/sites/$siteId',
                  params: { siteId: value },
                  search: { chatId, siteId: undefined },
                });
              }}
              items={sites.data?.map((site) => ({ value: site.id, label: site.name })) ?? []}
            />
          )}
        </div>
        {site?.domain && (
          <LinkButton
            href={`${location.protocol}//${site.domain}`}
            disabled={!site.published}
            external
            variant="ghost"
            size="sm"
            aria-label="View site"
            title="View published site"
          >
            <ArrowUpRight size={18} />
          </LinkButton>
        )}
      </div>
      <ErrorNotice error={sites.error} className="m-4" />
      {siteId ? (
        <>
          <nav className="site-panel-tabs" aria-label="Site tabs">
            {tabs.map((tab) => (
              <Link
                key={tab.section}
                to={tab.path}
                params={{ siteId }}
                search={{ chatId, siteId: undefined, preview: undefined }}
                activeOptions={{ exact: true }}
                aria-current={section === tab.section ? 'page' : undefined}
              >
                {tab.title}
              </Link>
            ))}
          </nav>
          {section === 'settings' && (
            <div className="site-panel-subnav">
              <Select<string>
                aria-label="Settings section"
                className="w-44 min-w-0"
                value={screen}
                onValueChange={(value) => {
                  const selected = settingsSections.find(([path]) => path === value);
                  if (selected) void navigate({ to: `/sites/$siteId/${selected[0]}`, params: { siteId } });
                }}
                items={Object.fromEntries(settingsSections)}
              />
            </div>
          )}
          {section === 'inbox' && (
            <nav className="site-panel-subnav" aria-label="Inbox">
              <Link
                to="/sites/$siteId/form-entries"
                params={{ siteId }}
                aria-current={screen === 'form-entries' ? 'page' : undefined}
              >
                Form entries
              </Link>
              <Link
                to="/sites/$siteId/site-assistant/inbox"
                params={{ siteId }}
                aria-current={screen === 'site-assistant' ? 'page' : undefined}
              >
                Visitor conversations
              </Link>
              <Link
                to="/sites/$siteId/reviews"
                params={{ siteId }}
                aria-current={screen === 'reviews' ? 'page' : undefined}
              >
                Reviews
              </Link>
            </nav>
          )}
          <SiteIdContext value={siteId}>
            <div
              className={`site-panel-content ${section === 'preview' ? 'site-panel-preview' : ''}`}
              key={siteId}
            >
              {search.preview ? <SitePreview /> : children}
            </div>
          </SiteIdContext>
        </>
      ) : null}
    </aside>
  );
}

export function SitePreview() {
  const siteId = useContext(SiteIdContext);
  const search = useSearch({ strict: false });
  const site = useQuery({ ...siteQuery(siteId ?? ''), enabled: Boolean(siteId) });
  if (site.isPending) return <Loading />;
  if (site.error) return <ErrorNotice error={site.error} className="m-4" />;
  const settings = site.data.objects.find((object) => object.kind === 'Settings')!;
  return (
    <Suspense fallback={<Loading />}>
      <Preview key={siteId} snapshot={site.data} locale={search.locale ?? settings.baseLocale} />
    </Suspense>
  );
}
